import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Table, {TableCell, TableRow, TableSectionHeader} from "@amzn/meridian/table";
import React, {useContext} from "react"
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close";
import check from "@amzn/meridian-tokens/base/icon/check-large"
import Link from "@amzn/meridian/link";
import {FormattedMessage} from "react-intl";
import {CapabilityContext} from "../global/context/CapabilityContext";
import {AR, ENTITY_TYPE, NOT_IN_CART} from "../util/Constants";
import {isARorSSD, getFCLMLink, convertSecondsToTimeFormat, chainWalk} from "../util/Util";
import {getDocumentURL} from "../handler/ParentDataHandler";
import Box from "@amzn/meridian/box";
import {getStationCode} from "../network/NetworkUtil";
import {GROCERY_PICK, RUSH_1_HR} from "../util/EntityStatusManager";
import {RouteDetailContext} from "./context/RouteDetailContext";

const CartContentV2 = (props) => {
    const {configViewModel} = useContext(CapabilityContext);
    const {viewModelRD} = useContext(RouteDetailContext);
    const stationConfig = configViewModel.stationConfig;
    const isNodeARorSSD = isARorSSD(configViewModel.capability);
    const preStageTime = chainWalk(() => props.picklist.pickAssociates[0].endTime, new Date().getTime());
    const stageTime = props.container.dwellDuration ? new Date().getTime() : props.container.stageTime;
    const dwellDuration = stageTime - preStageTime

    const isGroceryCart = props.picklist && props.picklist.workGroupSequenceList && props.picklist.workGroupSequenceList.some(
        workGroup => workGroup.workGroupType === GROCERY_PICK
    );
    const isRush1Hr = viewModelRD && viewModelRD.routeInfo && (viewModelRD.routeInfo.routeSpeed === RUSH_1_HR);
    const onContainerClicked = (containerType, containerId) => {
        getDocumentURL().then(documentUrl => {
            //const documentUrl = "https://logistics.amazon.co.uk/station/dashboard/pick";
            const searchURL = new URL(documentUrl.toString().replace('pick', 'search'));

            const startDate = new Date();
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date();
            endDate.setDate(startDate.getDate() + 1);
            endDate.setHours(0, 0, 0, 0);

            const optionsObj = {
                "type": containerType,
                "startDate": startDate.getTime(),
                "endDate": endDate.getTime(),
            }

            const shareableLink = "/detailPage/" + containerId + '/' + JSON.stringify(optionsObj);
            searchURL.searchParams.set("shareableLink", shareableLink);
            window.open(searchURL.href, "_blank")
        });
    }

    const isPicked = (container) => {
        if (!configViewModel.stationConfig.pickStageDecoupleEnabled && !configViewModel.stationConfig.routeDetailsTabViewEnabled) {
            return container.pickedV2;
        }
        return container.picked;
    }

    const getContainerLabel = (container, index) => {
        if (container.name === NOT_IN_CART) {
            return NOT_IN_CART;
        }
        if(stationConfig.parallelPickCartNumbering && !stationConfig.routeDetailsTabViewEnabled) {
            return `Cart ${container.routeSequenceNumber} (${container.name})`;
        }
        else {
            return `Cart ${index + 1} (${container.name})`;
        }
    }

    const renderPickedState = () => {
        return (!props.container.isNotOVCart && !isNodeARorSSD) || props.container.isDynamicRouteContainer ||
        (stationConfig.routeDetailsTabViewEnabled && isGroceryCart && !isRush1Hr);
    }

    const renderCartInfo = () => {
        if (props.container.name === NOT_IN_CART
            || !(stationConfig.parallelPickEnabled
                || stationConfig.highSPREnabled
                || stationConfig.pickStageDecoupleEnabled
                || configViewModel.capability === AR)) {
            return null;
        }

        const stationCode = getStationCode()

        return <Row width="100%" backgroundColor={"secondary"} spacingInset={"small"}>
            {/* STAGE LOCATION */}
            {stationConfig.parallelPickEnabled || stationConfig.highSPREnabled ?
                <Row>
                    <Text type="h100">
                        {stationConfig.splitStagingEnabled ?
                            <FormattedMessage id="cartContent.actualLocation" defaultMessage="Actual location"/>
                            :
                            <FormattedMessage id="cartContent.stagingLocation" defaultMessage="Staging location"/>
                        }
                    </Text>
                    <Text type="b200">
                        {props.container.stagingLocation}
                    </Text>
                </Row>
                : null
            }
            {stationConfig.splitStagingEnabled ?
                <Row>
                    <Text type="h100">
                        <FormattedMessage id="cartContent.dwellDuration" defaultMessage="Dwell Time"/>
                    </Text>
                    <Text type="b200">
                        {dwellDuration > 0 ? convertSecondsToTimeFormat(dwellDuration / 1000) : "..."}
                    </Text>
                </Row>
                : null
            }

            {/* STAGE ASSOCIATE */}
            {configViewModel.stationConfig.pickStageDecoupleEnabled ?
                <Row>
                    <Text type="h100">
                        <FormattedMessage id="cartContent.stageAssociate" defaultMessage="Stage Associate"/>
                    </Text>
                    {props.container.stageAssociate !== '...' ?
                        <Link href={getFCLMLink(props.container.stageAssociate, configViewModel.stationConfig.fclmLinkEnabled, stationCode)} type="secondary" target="_blank"
                              rel="noreferrer noopener">
                            {props.container.stageAssociate}
                        </Link>
                        : <Text type="b200">...</Text>
                    }
                </Row>
                : null
            }
            {/* Floor Location*/}
            {(configViewModel.capability === AR && props.container.floor) ?
                <Row>
                    <Text type="h100">
                        <FormattedMessage id="cartContent.floorLocation" defaultMessage="Floor Location"/>
                    </Text>
                    <Text type="b200">{props.container.floor}</Text>
                </Row> : null}
        </Row>
    }

    return (
        <Box width="100%" type={"outline"} spacingInset={"medium"}>

            {/* CART LABEL & OTHER INFO */}
            <Row alignmentVertical="stretch">
                <Row minWidth={"350px"}>
                    <Heading level={3} type="h500"
                             alignment="left">{getContainerLabel(props.container, props.index)}</Heading>
                </Row>
                {renderCartInfo()}
            </Row>

            <Row width="100%" spacingInset="small none none">
                <Table headerRows={1} showDividers={true} spacing="small">
                    {/* CART TABLE MAIN HEADER */}
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id="cartContent.itemId" defaultMessage="Item ID"/>
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id="cartContent.location" defaultMessage="Location"/>
                        </TableCell>
                        {(!props.container.isNotOVCart && isNodeARorSSD) || props.container.isDynamicRouteContainer ?
                            <TableCell>
                                <FormattedMessage id="cartContent.stowed" defaultMessage="Stowed"/>
                            </TableCell>
                            : null
                        }
                        {renderPickedState() || stationConfig.dropToCartEnabled ?
                            <TableCell>
                                <FormattedMessage id="cartContent.picked" defaultMessage="Picked"/>
                            </TableCell>
                            : null
                        }
                        <TableCell>
                            <FormattedMessage id="cartContent.staged" defaultMessage="Staged"/>
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id="cartContent.departed" defaultMessage="Departed"/>
                        </TableCell>
                    </TableRow>

                    {/* CART TABLE BAG SECTION HEADER */}
                    {props.container.bags.length > 0 ? (
                        <TableSectionHeader>
                            <FormattedMessage id="pick.bags" defaultMessage="Bags"/>: {props.container.bags.length}
                        </TableSectionHeader>
                    ) : null}

                    {/* CART TABLE BAG ROWS */}
                    {props.container.bags.map(bag => (
                        <TableRow key={bag.label}>
                            <TableCell>
                                <Link onClick={() => onContainerClicked(ENTITY_TYPE.CONTAINER, bag.label)}
                                      type="secondary"
                                      target="_blank" rel="noreferrer noopener">
                                    {bag.label}
                                </Link>
                            </TableCell>
                            <TableCell>{bag.location}</TableCell>
                            {props.container.isDynamicRouteContainer ?
                                <TableCell>
                                    <Text>
                                        {bag.stowed ? <Icon className="picked-success" tokens={check}/> :
                                            <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                    </Text>
                                </TableCell>
                                : null}
                            {renderPickedState() ?
                                <TableCell>
                                    <Text>
                                        {isPicked(bag) ? <Icon className="picked-success" tokens={check}/> :
                                            <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                    </Text>
                                </TableCell>
                                : null
                            }
                            <TableCell>
                                <Text>
                                    {bag.staged ? <Icon className="picked-success" tokens={check}/> :
                                        <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                </Text>
                            </TableCell>
                            <TableCell>
                                <Text>
                                    {bag.departed ? <Icon className="picked-success" tokens={check}/> :
                                        <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                </Text>
                            </TableCell>
                        </TableRow>
                    ))}

                    {/* CART TABLE PACKAGE SECTION HEADER */}
                    {props.container.packages.length > 0 ? (
                        <TableSectionHeader>
                            <FormattedMessage id="pick.packages"
                                              defaultMessage="Packages"/>: {props.container.packages.length}
                        </TableSectionHeader>
                    ) : null}

                    {/* CART TABLE PACKAGE ROWS */}
                    {props.container.packages.map(packages => (
                        <TableRow key={packages.label}>
                            <TableCell>
                                <Link onClick={() => onContainerClicked(ENTITY_TYPE.PACKAGE, packages.label)}
                                      type="secondary"
                                      target="_blank" rel="noreferrer noopener">
                                    {packages.label}
                                </Link>
                            </TableCell>
                            <TableCell>{packages.location}</TableCell>
                            {(!props.container.isNotOVCart  && isNodeARorSSD) || props.container.isDynamicRouteContainer ?
                                <TableCell>
                                    <Text>
                                        {packages.stowed ? <Icon className="picked-success" tokens={check}/> :
                                            <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                    </Text>
                                </TableCell>
                                : null}
                            {renderPickedState() || stationConfig.dropToCartEnabled ?
                                <TableCell>
                                    <Text>
                                        {isPicked(packages) ? <Icon className="picked-success" tokens={check}/> :
                                            <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                    </Text>
                                </TableCell>
                                : null
                            }
                            <TableCell>
                                <Text>
                                    {packages.staged ? <Icon className="picked-success" tokens={check}/> :
                                        <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                </Text>
                            </TableCell>
                            <TableCell>
                                <Text>
                                    {packages.departed ? <Icon className="picked-success" tokens={check}/> :
                                        <Icon className="picked-failed" tokens={closeMediumTokens}/>}
                                </Text>
                            </TableCell>
                        </TableRow>
                    ))}
                </Table>
            </Row>
        </Box>
    );
}

export default CartContentV2


// WEBPACK FOOTER //
// ./src/components/routeDrillDown/CartContentV2.js